import { Receipt } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { useSubscriptions } from "@/hooks/useSubscriptions";
import { formatDate } from "@/lib/utils";
import type { SubscriptionDto } from "@/types/api";

/**
 * Renders the account's full subscription history. Desktop: table.
 * Mobile: stacked card list (tables overflow on narrow screens).
 */
export function SubscriptionHistoryCard() {
  const { data, isLoading, error } = useSubscriptions();

  return (
    <Card>
      <CardHeader>
        <CardTitle>Billing history</CardTitle>
        <CardDescription>
          Every subscription on this account, newest first.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <HistorySkeleton />
        ) : error ? (
          <p className="py-6 text-center text-sm text-muted-foreground">
            Couldn't load history. Try again later.
          </p>
        ) : !data || data.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            {/* Desktop ≥ md */}
            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Plan</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Cycle</TableHead>
                    <TableHead>Started</TableHead>
                    <TableHead>Renews / Ends</TableHead>
                    <TableHead>Order ref</TableHead>
                    <TableHead className="text-right">Price</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.map(s => <SubscriptionRow key={s.id} sub={s} />)}
                </TableBody>
              </Table>
            </div>

            {/* Mobile < md */}
            <div className="grid gap-3 md:hidden">
              {data.map(s => <SubscriptionMobileCard key={s.id} sub={s} />)}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function SubscriptionRow({ sub }: { sub: SubscriptionDto }) {
  return (
    <TableRow>
      <TableCell>
        <p className="font-medium text-foreground">{sub.plan_name}</p>
        <p className="text-xs text-muted-foreground capitalize">{sub.plan_type}</p>
      </TableCell>
      <TableCell><StatusBadge status={sub.status} /></TableCell>
      <TableCell className="text-sm text-muted-foreground">{cycleLabel(sub.billing_months)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{formatDate(sub.started_at)}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {sub.status === "cancelled"
          ? formatDate(sub.cancelled_at)
          : formatDate(sub.renews_at)}
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {sub.hostshop_order_reference ?? "—"}
      </TableCell>
      <TableCell className="text-right text-sm font-medium">
        {priceLabel(sub)}
      </TableCell>
    </TableRow>
  );
}

function SubscriptionMobileCard({ sub }: { sub: SubscriptionDto }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <p className="font-medium text-foreground">{sub.plan_name}</p>
          <p className="mt-0.5 text-xs uppercase tracking-wider text-muted-foreground">{sub.plan_type}</p>
        </div>
        <StatusBadge status={sub.status} />
      </div>
      <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
        <Cell label="Cycle"   value={cycleLabel(sub.billing_months)} />
        <Cell label="Price"   value={priceLabel(sub)} />
        <Cell label="Started" value={formatDate(sub.started_at)} />
        <Cell label={sub.status === "cancelled" ? "Cancelled" : "Renews"}
              value={formatDate(sub.status === "cancelled" ? sub.cancelled_at : sub.renews_at)} />
      </div>
      {sub.hostshop_order_reference && (
        <p className="mt-3 truncate font-mono text-xs text-muted-foreground">
          Order {sub.hostshop_order_reference}
        </p>
      )}
    </div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm font-medium text-foreground">{value}</p>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const variant: React.ComponentProps<typeof Badge>["variant"] =
    status === "active"    ? "success" :
    status === "cancelled" ? "destructive" :
    "outline";
  return <Badge variant={variant} className="uppercase">{status}</Badge>;
}

function cycleLabel(months: number) {
  return months === 12 ? "Annual" : months === 3 ? "Quarterly" : "Monthly";
}

function priceLabel(sub: SubscriptionDto): string {
  const price = sub.billing_months === 12 ? sub.price_annually   ?? sub.price_monthly * 12
              : sub.billing_months === 3  ? sub.price_quarterly  ?? sub.price_monthly * 3
              : sub.price_monthly;
  const currency = sub.currency || "USD";
  const cycle = sub.billing_months === 12 ? "/yr" : sub.billing_months === 3 ? "/qtr" : "/mo";
  return `${currency === "USD" ? "$" : ""}${Math.round(price).toLocaleString()}${cycle}`;
}

function HistorySkeleton() {
  return (
    <div className="space-y-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-12 w-full" />
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-muted-foreground">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
        <Receipt className="h-5 w-5" />
      </div>
      <p>No subscriptions on this account yet.</p>
      <p className="text-xs">When you buy a plan, it'll show up here.</p>
    </div>
  );
}
